import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getTalentos } from '../services/api';
import { Instagram } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';
import TalentModal from '../components/TalentModal';
import { talentMatchesAnyVertical, parseVerticals } from '../components/VerticalFilterSelect';
import CountryFlag from '../components/CountryFlag';

/* ── Design tokens ─────────────────────────────────────────── */
const BRAND  = '#742263';
const CREAM  = '#FFFDC4';
const BG     = '#0A0509';

/* ── Helpers ────────────────────────────────────────────────── */
const formatFollowers = (raw) => {
  if (!raw) return null;
  const n = parseInt(String(raw).replace(/[^0-9]/g, ''), 10);
  if (isNaN(n) || n === 0) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${Math.round(n / 1_000)}K`;
  return String(n);
};

const collectVerticals = (talentos) => {
  const map = new Map();
  talentos.forEach((t) => {
    parseVerticals(t.vertical).forEach((v) => {
      map.set(v.toLowerCase(), v);
    });
  });
  return Array.from(map.values()).sort();
};

/* ── Pill component ─────────────────────────────────────────── */
const Pill = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-300"
    style={active
      ? { backgroundColor: BRAND, color: '#fff', border: `1px solid ${BRAND}` }
      : { backgroundColor: 'transparent', color: '#71717a', border: '1px solid rgba(255,255,255,0.1)' }
    }
  >
    {label}
  </button>
);

/* ── Main page ──────────────────────────────────────────────── */
const NewTalentos = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [talentos, setTalentos]               = useState([]);
  const [filteredTalentos, setFilteredTalentos] = useState([]);
  const [searchTerm, setSearchTerm]           = useState('');
  const [verticalFilter, setVerticalFilter]   = useState([
    searchParams.get('v1') || '__ALL__',
    searchParams.get('v2') || '__ALL__',
  ]);
  const [loading, setLoading]         = useState(true);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [searchFocused, setSearchFocused] = useState(false);

  /* ── lógica inalterada ──────────────────────────────────── */
  const handleVerticalChange = (pair) => {
    setVerticalFilter(pair);
    const params = new URLSearchParams();
    const [v1, v2] = pair || [];
    if (v1 && v1 !== '__ALL__') params.set('v1', v1);
    if (v2 && v2 !== '__ALL__') params.set('v2', v2);
    setSearchParams(params, { replace: true });
  };

  const fetchTalentos = useCallback(async () => {
    try {
      const data = await getTalentos();
      setTalentos(data);
    } catch (err) {
      console.error('Failed to fetch talentos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTalentos(); }, [fetchTalentos]);

  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const filtered = talentos
      .filter((t) => talentMatchesAnyVertical(t, verticalFilter))
      .filter((t) => (!term ? true : t.name.toLowerCase().includes(term)));
    setFilteredTalentos(filtered);
  }, [searchTerm, talentos, verticalFilter]);
  /* ── fin lógica ────────────────────────────────────────── */

  const verticals = useMemo(() => collectVerticals(talentos), [talentos]);

  const totalReach = useMemo(() => {
    let sum = 0;
    talentos.forEach((t) => {
      const ig = parseInt(String(t.instagram_followers || '').replace(/[^0-9]/g, ''), 10) || 0;
      const tk = parseInt(String(t.tiktok_followers   || '').replace(/[^0-9]/g, ''), 10) || 0;
      sum += ig + tk;
    });
    return sum;
  }, [talentos]);

  const activeFilters = verticalFilter.filter((v) => v && v !== '__ALL__');

  const togglePill = (label) => {
    if (activeFilters.includes(label)) {
      handleVerticalChange(['__ALL__', '__ALL__']);
    } else if (activeFilters.length === 0) {
      handleVerticalChange([label, '__ALL__']);
    } else if (activeFilters.length === 1) {
      handleVerticalChange([activeFilters[0], label]);
    } else {
      handleVerticalChange([label, '__ALL__']);
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: BG, fontFamily: "'Inter', 'DM Sans', sans-serif" }}
    >
      {/* ── Radial depth gradient ──────────────────────────── */}
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          background: `radial-gradient(ellipse 80% 60% at 50% -10%, rgba(116,34,99,0.18) 0%, transparent 70%)`,
        }}
      />

      <div className="relative z-10">

        {/* ════════════════════════════════════ HERO */}
        <section className="pt-32 pb-12 px-6">
          <div className="max-w-7xl mx-auto">

            {/* eyebrow */}
            <div
              className="flex items-center gap-3 mb-8 animate-fade-in-up"
              style={{ animationDelay: '0ms' }}
            >
              <div className="h-px w-8" style={{ backgroundColor: BRAND }} />
              <span
                className="text-xs uppercase tracking-[0.3em] font-semibold"
                style={{ color: BRAND }}
              >
                Nuestro Roster
              </span>
            </div>

            {/* headline */}
            <h1
              className="font-bold tracking-tight leading-[1.1] max-w-4xl mb-10 animate-fade-in-up"
              style={{
                animationDelay: '80ms',
                fontSize: 'clamp(2rem, 5vw, 3.75rem)',
                background: `linear-gradient(135deg, #ffffff 0%, ${CREAM} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Los talentos que conectan marcas y audiencias en toda LATAM
            </h1>

            {/* metrics */}
            <div
              className="flex items-end gap-12 mb-14 animate-fade-in-up"
              style={{ animationDelay: '160ms' }}
            >
              <div>
                <div
                  className="text-4xl font-bold tabular-nums"
                  style={{ color: CREAM }}
                  data-testid="metric-total-talentos"
                >
                  {loading ? '—' : talentos.length}
                </div>
                <div className="text-xs uppercase tracking-[0.2em] mt-1.5" style={{ color: '#52525b' }}>
                  Talentos Exclusivos
                </div>
              </div>

              {totalReach > 0 && (
                <div>
                  <div className="text-4xl font-bold tabular-nums" style={{ color: CREAM }}>
                    {formatFollowers(String(totalReach))}
                  </div>
                  <div className="text-xs uppercase tracking-[0.2em] mt-1.5" style={{ color: '#52525b' }}>
                    Alcance Total Combinado
                  </div>
                </div>
              )}
            </div>

            {/* search */}
            <div
              className="max-w-sm animate-fade-in-up"
              style={{ animationDelay: '240ms' }}
            >
              <input
                type="text"
                placeholder="Buscar talento..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                className="w-full text-white placeholder-zinc-600 text-sm px-5 py-3 rounded-full outline-none transition-all duration-300"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  border: `1px solid ${searchFocused ? BRAND : 'rgba(255,255,255,0.08)'}`,
                  boxShadow: searchFocused ? `0 0 0 4px rgba(116,34,99,0.15)` : 'none',
                }}
                data-testid="talent-search-input"
              />
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════ PILL FILTERS */}
        <div
          className="px-6 pb-10 animate-fade-in-up"
          style={{ animationDelay: '320ms' }}
        >
          <div className="max-w-7xl mx-auto">
            <div
              className="flex items-center gap-2 overflow-x-auto pb-1"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              data-testid="vertical-filter-pills"
            >
              <Pill
                label="Todos"
                active={activeFilters.length === 0}
                onClick={() => handleVerticalChange(['__ALL__', '__ALL__'])}
              />
              {verticals.map((v) => (
                <Pill
                  key={v}
                  label={v}
                  active={activeFilters.includes(v)}
                  onClick={() => togglePill(v)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* ════════════════════════════════════ TALENT GRID */}
        <section className="px-6 pb-28">
          <div className="max-w-7xl mx-auto">

            {loading ? (
              <div className="flex flex-col items-center justify-center py-40">
                <div
                  className="w-10 h-10 rounded-full border-2 animate-spin"
                  style={{ borderColor: `${BRAND} transparent transparent transparent` }}
                />
                <p className="text-zinc-600 mt-5 text-xs uppercase tracking-[0.25em]">
                  Cargando talentos...
                </p>
              </div>
            ) : (
              <>
                {/* count */}
                <p
                  className="text-zinc-600 text-xs uppercase tracking-[0.2em] mb-8"
                  data-testid="talent-count"
                >
                  {filteredTalentos.length} / {talentos.length} talentos
                </p>

                {/* grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
                  {filteredTalentos.map((talento, i) => {
                    const igF   = formatFollowers(talento.instagram_followers);
                    const tkF   = formatFollowers(talento.tiktok_followers);
                    const verts = parseVerticals(talento.vertical).slice(0, 2);

                    return (
                      <div
                        key={talento.id}
                        className="group relative overflow-hidden rounded-2xl cursor-pointer animate-fade-in-up"
                        style={{
                          border: '1px solid rgba(116,34,99,0.2)',
                          boxShadow: '0 20px 40px -12px rgba(0,0,0,0.8)',
                          animationDelay: `${i * 40}ms`,
                        }}
                        onClick={() => setSelectedTalent(talento)}
                        data-testid={`talent-card-${talento.id}`}
                      >
                        {/* aspect-ratio wrapper */}
                        <div className="aspect-[3/4] relative">

                          {/* photo */}
                          <img
                            src={talento.image}
                            alt={talento.name}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                          />

                          {/* gradient overlay */}
                          <div
                            className="absolute inset-0 transition-opacity duration-500"
                            style={{
                              background: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.35) 45%, transparent 100%)',
                            }}
                          />

                          {/* hover: intensify overlay */}
                          <div
                            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                            style={{
                              background: 'linear-gradient(to top, rgba(0,0,0,0.97) 0%, rgba(0,0,0,0.55) 55%, transparent 100%)',
                            }}
                          />

                          {/* country flag */}
                          <CountryFlag
                            talento={talento}
                            className="absolute top-3 right-3 z-10"
                          />

                          {/* info block */}
                          <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 z-10">

                            {/* name */}
                            <h3
                              className="font-bold uppercase text-center text-xs md:text-sm leading-tight text-white drop-shadow-lg transition-transform duration-300"
                              style={{ letterSpacing: '0.06em' }}
                            >
                              {talento.name}
                            </h3>

                            {/* verticals — fade on hover */}
                            {verts.length > 0 && (
                              <p
                                className="text-center mt-1.5 text-[10px] uppercase tracking-wide transition-all duration-300 translate-y-1 opacity-60 group-hover:translate-y-0 group-hover:opacity-100"
                                style={{ color: '#a1a1aa' }}
                              >
                                {verts.join(' · ')}
                              </p>
                            )}

                            {/* social links — siempre visibles, se iluminan en hover */}
                            {(talento.instagram_url || talento.tiktok_url) && (
                              <div className="flex items-center justify-center gap-2 mt-2">
                                {talento.instagram_url && (
                                  <a
                                    href={talento.instagram_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all duration-300 text-[10px] font-medium"
                                    style={{
                                      backgroundColor: 'rgba(255,255,255,0.1)',
                                      color: '#d4d4d8',
                                      border: '1px solid rgba(255,255,255,0.12)',
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = BRAND; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = BRAND; }}
                                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#d4d4d8'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                                    data-testid={`talent-ig-link-${talento.id}`}
                                  >
                                    <Instagram size={10} />
                                    {igF && <span>{igF}</span>}
                                  </a>
                                )}
                                {talento.tiktok_url && (
                                  <a
                                    href={talento.tiktok_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all duration-300 text-[10px] font-medium"
                                    style={{
                                      backgroundColor: 'rgba(255,255,255,0.1)',
                                      color: '#d4d4d8',
                                      border: '1px solid rgba(255,255,255,0.12)',
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = BRAND; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = BRAND; }}
                                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#d4d4d8'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                                    data-testid={`talent-tiktok-link-${talento.id}`}
                                  >
                                    <FaTiktok size={10} />
                                    {tkF && <span>{tkF}</span>}
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* empty state */}
                {filteredTalentos.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-40">
                    <p className="text-zinc-600 text-xs uppercase tracking-[0.25em]">
                      No se encontraron talentos
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

      </div>{/* /z-10 */}

      {/* talent modal — sin cambios */}
      {selectedTalent && (
        <TalentModal talento={selectedTalent} onClose={() => setSelectedTalent(null)} />
      )}
    </div>
  );
};

export default NewTalentos;
